import React from "react"
import { Marker, Tooltip, Circle } from "react-leaflet"
import L from "leaflet"

/**
 * Shared functions between all map pages.
 */

/**
 * Convert kilometers into latitude.
 *
 * Source: https://stackoverflow.com/a/57159311
 */
export function km2lat(latDistance) {
    return latDistance / 110.574
}

/**
 * Convert kilometers into longitude.
 *
 * Source: https://stackoverflow.com/a/57159311
 */
export function km2lng(longDistance) {
    const lat = km2lat(longDistance)
    return longDistance / (111.32 * Math.cos(lat / Math.PI / 180))
}

/**
 * Commonly used icons for markers
 */
export const MarkerIcons = {
    bolt: new L.Icon({
        iconUrl: require("../marker_images/bolt.svg"),
        iconSize: [30, 48],
    }),

    crane: new L.Icon({
        iconUrl: require("../marker_images/icons8-crane.svg"),
        iconSize: [30, 48],
    }),
}

/**
 * Renders common map markers for data from the Lagebild XML file.
 */
export function MapMarkers({ displaymode, objects, selectedElement }) {
    if (objects) {
        return objects.map((obj, i) => {
            const isSelected = selectedElement === obj

            let color

            switch (obj.type) {
                case "stations":
                    color = isSelected ? "#FF0000" : "#FF0000"
                    break

                case "grosskunden":
                    if (displaymode === "powerQualityDrop") {
                        color = isSelected ? "#00FF00" : "#00FF00"
                    } else {
                        color = isSelected ? "#FFC107" : "#FFC107"
                    }
                    break

                case "kritinfras":
                    color = isSelected ? "#DD3546" : "#DD3546"
                    break

                case "construction-sites":
                    color = isSelected ? "#ffe700" : "#ffe700"
                    break

                default:
                    color = isSelected ? "#0000FF" : "#0000FF"
            }

            const sharedProps = {
                stroke: true,
                color: color,
                opacity: 0.8,
                weight: isSelected ? 6 : 3,
                fill: true,
                fillColor: color,
                fillOpacity: 0.35,
            }

            // https://leafletjs.com/reference-1.5.0.html#circle
            if (
                displaymode === "powerQualityDrop" &&
                obj.type === "grosskunden"
            ) {
                return (
                    <Marker
                        key={`${obj.netId}/${i}`}
                        position={[obj.lat, obj.lng]}
                        icon={MarkerIcons.bolt}
                    >
                        <Tooltip>{obj.name1}</Tooltip>
                    </Marker>
                )
            } else if (obj.type === "construction-sites") {
                // https://leafletjs.com/reference-1.5.0.html#circle
                /**
                 let rectWidth = km2lat(obj.size || 1)
                 let rectHeight = km2lng(obj.size || 1)
                */

                return (
                    <Circle
                        key={`${obj.netId}/${i}`}
                        center={[obj.lat, obj.lng]}
                        radius={obj.size || 10}
                        {...sharedProps}
                    >
                        <Tooltip>{obj.gemeinde} {obj.info}, {obj.timestampHuman} - {obj.timestampEndHuman}</Tooltip>
                    </Circle>
                    /**
                    <Marker
                        key={`${obj.netId}/${i}`}
                        position={[obj.lat, obj.lng]}
                        icon={MarkerIcons.crane}
                    >
                        <Tooltip>{obj.gemeinde} {obj.info}, {obj.timestampHuman} - {obj.timestampEndHuman}</Tooltip>
                    </Marker>

                    <Rectangle
                        key={`${obj.netId}/${i}`}
                        bounds={[
                            [obj.lat - rectWidth / 2, obj.lng - rectHeight / 2],
                            [obj.lat + rectWidth, obj.lng + rectHeight],
                        ]}
                        {...sharedProps}
                    />
                    */
                )

            } else if (obj.type === "stations") {
                if (obj.subType === "STATION") {
                    return (
                       <Circle
                            key={`${obj.netId}/${i}`}
                            center={[obj.lat, obj.lng]}
                            radius={obj.size || 100}
                            {...sharedProps}
                        >
                            <Tooltip>Störung - {obj.gemeinde} {obj.timestampHuman}</Tooltip>
                        </Circle>
                    )
                } else {
                    //-- Actually type "ADDRESS" only
                    return (
                        <Circle
                            key={`${obj.netId}/${i}`}
                            center={[obj.lat, obj.lng]}
                            radius={obj.size || 50}
                            {...sharedProps}
                        >
                            <Tooltip>Störung - {obj.gemeinde} {obj.timestampHuman}</Tooltip>
                        </Circle>
                    )
                }

            } else {
                return (
                    <Marker
                        key={`${obj.netId}/${i}`}
                        position={[obj.lat, obj.lng]}
                        icon={MarkerIcons.crane}
                    >
                        <Tooltip>{obj.gemeinde} {obj.timestampHuman}</Tooltip>
                    </Marker>
                )
            }
        })
    }

    return null
}

/**
 * Schedules Leaflet.invalidateSize
 */
function scheduleMapFix(mapRef, timeout) {
    setTimeout(() => {
        if (mapRef.current) {
            mapRef.current.leafletElement.invalidateSize()
        }
    }, timeout)
}

/*
 * This hook is a hack that fixes a bug that sometimes after reloading the map,
 * leaflet renders a broken tile layer until it's resized again.
 *
 * We do this three times because sometimes it doesn't work at first try.
 *
 * This bug has been in leaflet since 2012:
 *  - https://github.com/Leaflet/Leaflet/issues/694
 *  - https://github.com/Leaflet/Leaflet/issues/4835
 */
export function useLeafletRenderFix(mapRef) {
    React.useEffect(function() {
        scheduleMapFix(mapRef, 33) // 1 frame at 30 FPS
        scheduleMapFix(mapRef, 66) // 2 frames at 30 FPS
        scheduleMapFix(mapRef, 132) // 4 frames at 30 FPS
    })
}
